import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

import DeexPending from "../../components/DeexPending";
import { useBusinesses } from "../../hooks/use-businesses";
import { useMediaQuery } from "../../hooks/use-mobile";
import { cn } from "../../utils/cn";
import Banks from "./Banks";
import BusinessDetails from "./BusinessDetails";
import Documents from "./Documents";
import Managers from "./Managers";
import Profile from "./Profile";
import Security from "./Security";
import Support from "./Support";
import {
  DEFAULT_SETTINGS_TAB,
  isSettingsTab,
  SETTINGS_TABS,
} from "./settings-tabs";

/** Matches the `lgss` Tailwind breakpoint — above it, rail and body sit side by side. */
const WIDE_QUERY = "(min-width: 976px)";

const TAB_BODIES: Record<string, React.ComponentType> = {
  profile: Profile,
  business: BusinessDetails,
  security: Security,
  banks: Banks,
  documents: Documents,
  managers: Managers,
  support: Support,
};

const Settings = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const isWide = useMediaQuery(WIDE_QUERY);

  // The shell owns the business fetch so tab bodies don't each re-request it.
  const { hasBusiness, isLoading } = useBusinesses();

  // An unknown slug is a dead end — send it somewhere real.
  if (tab && !isSettingsTab(tab)) {
    return <Navigate to={`/settings/${DEFAULT_SETTINGS_TAB}`} replace />;
  }

  // Wide screens always show a tab beside the rail; narrow screens use
  // /settings as a drill-down list, so only redirect on wide.
  if (!tab && isWide) {
    return <Navigate to={`/settings/${DEFAULT_SETTINGS_TAB}`} replace />;
  }

  const active = SETTINGS_TABS.find((item) => item.slug === tab);
  const Body = tab ? TAB_BODIES[tab] : undefined;

  // Narrow + a tab selected = detail view; the rail is replaced by the body.
  const showRail = isWide || !tab;
  const showBody = !!Body && (isWide || !!tab);

  return (
    <div className="mx-auto w-full max-w-[1400px] font-manrope">
      <div className="flex items-center gap-3">
        {!isWide && tab && (
          <button
            type="button"
            onClick={() => navigate("/settings")}
            aria-label="Back to settings"
            className="-ml-1 rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ChevronLeft className="size-5" />
          </button>
        )}
        <div>
          <h1 className="font-sora text-[24px] font-bold text-foreground">
            {!isWide && active ? active.title : "Settings"}
          </h1>
          <p className="mt-1 text-[14px] font-medium text-muted-foreground">
            {!isWide && active
              ? active.description
              : "Manage your profile, business and payout preferences."}
          </p>
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-6 lgss:flex-row lgss:items-start">
        {showRail && (
          <nav
            aria-label="Settings sections"
            className="w-full shrink-0 lgss:sticky lgss:top-6 lgss:w-[260px]"
          >
            <ul className="flex flex-col gap-1">
              {SETTINGS_TABS.map((item) => {
                const isActive = item.slug === tab;
                return (
                  <li key={item.slug}>
                    <button
                      type="button"
                      onClick={() => navigate(`/settings/${item.slug}`)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                        // On the narrow drill-down list every row is a plain
                        // card; on the rail the current tab is highlighted.
                        isActive && isWide
                          ? "border-transparent bg-accent text-primary"
                          : "border-border bg-background hover:bg-muted"
                      )}
                    >
                      <item.icon
                        className={cn(
                          "size-5 shrink-0",
                          isActive && isWide
                            ? "text-primary"
                            : "text-muted-foreground"
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-semibold">
                          {item.title}
                        </span>
                        <span className="block truncate text-[12px] font-medium text-muted-foreground">
                          {item.description}
                        </span>
                      </span>
                      <ChevronRight className="size-4 shrink-0 text-muted-foreground lgss:hidden" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}

        {showBody && Body && (
          <div className="min-w-0 flex-1">
            <Body />
          </div>
        )}
      </div>

      {!isLoading && !hasBusiness ? <DeexPending /> : null}
    </div>
  );
};

export default Settings;
